<template>
    <div class="u-page-wrapper pb-56">
        <div class="u-page-headline mb-0 u-flex u-flex--space-between u-flex--vertical-center">
            <h2 class="u-size-body">
                <i class="el-icon-star-on" aria-hidden="true"></i>
                Adressen
            </h2>

            <el-button type="text" icon="el-icon-plus" circle @click="showAddModal = true" />
        </div>

        <el-input class="search mb-4" placeholder="Suchen..." v-model="searchTerm" clearable />

        <template v-if="filteredAddresses.length">
            <swipe-list
                ref="list"
                class="card"
                :items="sortedAddresses"
                item-key="id"
            >
                <template v-slot="{ item: { id, name, address } }">
                    <div
                        class="address-item p-16 u-flex u-flex--vertical-center u-flex--space-between u-pointer"
                        @click="sendAddressToCar(address)"
                    >
                        <div class="mr-16">
                            <h3 class="mb-4 u-bold u-size-body text-primary">{{ name }}</h3>
                            <span class="u-break-word">{{ address }}</span>
                        </div>
                        <el-button type="primary" icon="el-icon-map-location" size="small" />
                    </div>
                </template>

                <template #right="{ item }">
                    <div class="u-flex ml-16">
                        <el-button
                            class="swipeout-action__button"
                            type="success"
                            icon="el-icon-edit"
                            @click="onEdit(item.id)"
                        />
                    </div>
                    <div class="u-flex">
                        <el-button
                            class="swipeout-action__button"
                            type="primary"
                            icon="el-icon-delete"
                            @click="onDelete(item.id)"
                        />
                    </div>
                </template>
            </swipe-list>
        </template>
        <p v-else-if="!addresses.length" class="u-size-14 mt-16">Du hast noch keine Adressen hinzugefügt.</p>
        <p v-else class="u-size-14 mt-16">Es wurden keine Adressen gefunden.</p>

        <add-address-modal
            v-model="showAddModal"
            :form-data="editData"
            @reload="loadAddresses"
        />
    </div>
</template>

<script>
import { includes, pipe, prop, toLower } from 'ramda'
import AddAddressModal from '@/components/modals/AddAddressModal'
import { SwipeList } from 'vue-swipe-actions'
import Appwrite from '@/assets/appwrite'

export default {
    components: { AddAddressModal, SwipeList },

    data () {
        return {
            addresses: [],
            showAddModal: false,
            editData: null,
            searchTerm: '',
        }
    },

    computed: {
        filteredAddresses () {
            const fieldsToCheck = ['name', 'address']

            return this.addresses.filter(address => {
                return fieldsToCheck.some(field => pipe(
                    prop(field),
                    toLower,
                    includes(toLower(this.searchTerm)),
                )(address))
            })
        },

        sortedAddresses () {
            const addresses = this.filteredAddresses // Do not change original array
            if (!addresses) return addresses

            return addresses.sort(this.sortAddressesByName)
        },
    },

    watch: {
        showAddModal () {
            if (!this.showAddModal) {
                this.editData = null
            }
        },
    },

    async mounted () {
        await this.loadAddresses()
    },

    methods: {
        async loadAddresses () {
            try {
                const { documents } = await Appwrite.databases.listDocuments(Appwrite.DB_NAME, 'address')
                if (!documents.length) return []

                this.addresses = documents.map(x => ({ ...x, id: x.$id, created: x.$createdAt }))
                this.addresses = this.addresses.map(address => ({ ...address, address: `${address.street}, ${address.zip} ${address.city}` }))
            } catch (e) {
                this.$notify({
                    title: 'Error',
                    message: e.message,
                    type: 'error',
                })
            }
        },

        async sendAddressToCar (address) {
            try {
                const encodedAddress = encodeURIComponent(`${address}, Deutschland, DE`)
                const text = `https://www.google.com/maps/place/${encodedAddress}`

                await navigator.share({ text })
            } catch (e) {
                if (e.message.startsWith('Abort')) return

                console.log(e)
                console.log('Tried sharing:', address)

                this.$notify({
                    title: 'Error',
                    message: e.message,
                    type: 'error',
                })
            }
        },

        sortAddressesByName (a, b) {
            const nameA = a.name.toLowerCase()
            const nameB = b.name.toLowerCase()

            if (nameA === nameB) return 0

            return nameA < nameB ? -1 : 1
        },

        onEdit (itemId) {
            this.editData = this.addresses.find(x => x.id === itemId) || null
            this.showAddModal = true
        },

        onDelete (itemId) {
            this.$confirm('Willst du den Eintrag wirklich löschen?', 'Achtung', {
                confirmButtonText: 'Ja',
                cancelButtonText: 'Nein',
                type: 'warning',
            }).then(async () => {
                try {
                    await Appwrite.databases.deleteDocument(Appwrite.DB_NAME, 'address', itemId)
                    await this.loadAddresses()
                } catch (e) {
                    this.$notify({
                        title: 'Error',
                        message: e.message,
                        type: 'error',
                    })
                }
            }).catch(() => {
                // Do nothing
            })
        },
    },
}
</script>

<style lang="scss" scoped>
.search /deep/ .el-input__inner {
    border-radius: 0 !important;
}

.address-item {
    margin: 0 (-$spacing-unit-16) $spacing-unit-8;
    user-select: none; /* Disable text selection */

    @include interaction() {
        background-color: lighten($page-background-color, 10%);
    }
}

.swipeout-action {
    &__button {
        border-radius: 0 !important;
    }
}
</style>
